import * as yup from 'yup';

/**
 * E48 · Functional analysis — the both-ends input codecs of the five mutations (plan 03 §2.1 #3): the phone (before it saves or queues),
 * the portal and — as the shield's inputRule — the API run the SAME schema. SHAPE only; the business rules (at most three active
 * targets, a target that is not the caller's, an urge outside 0–10, a moment too far back, a day before the change starts, who may close
 * what) are the deciders', where they are binding and answer with the board's coded refusals in the person's language. `performer` is
 * ABSENT on purpose: the caller is the performer. The lists mirror `@cc/domain` `functionalAnalysis/constants.ts` (kept as literals
 * here so the schema package stays free of the domain package).
 */
export const FA_MEASURE_VALUES = [
  'frequency',
  'duration',
  'intensity',
] as const;
export const FA_FUNCTION_VALUES = [
  'escapeAvoidance',
  'attention',
  'tangible',
  'automaticRelief',
  'unknown',
] as const;
export const FA_VULNERABILITY_KIND_VALUES = [
  'sleep',
  'tired',
  'hunger',
  'illness',
  'pain',
  'substances',
  'hardDay',
  'other',
] as const;
export const FA_LINK_KIND_VALUES = [
  'event',
  'thought',
  'feeling',
  'body',
  'action',
] as const;
export const FA_TOOL_VALUES = [
  'thoughtTest',
  'copingCard',
  'exposure',
  'plannedActivity',
  'crisisKit',
  'meditation',
  'abcModel',
  'worksheet',
] as const;
export const FA_CHANGE_DONE_VALUES = ['yes', 'partly', 'no'] as const;

const uuidV7 = yup
  .string()
  .trim()
  .matches(
    /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
  )
  .required();

const userRef = yup
  .string()
  .trim()
  .matches(/^users\/[A-Za-z0-9_-]{1,64}$/);

const idRef = yup.string().trim().min(1).max(128);

const localDate = yup
  .string()
  .trim()
  .matches(/^\d{4}-\d{2}-\d{2}$/);

const words = (max: number) => yup.string().max(max).nullable();

export const setTargetBehaviorInputSchema = yup.object({
  subject: userRef.nullable(),
  targetId: idRef.nullable(),
  // a blank name is the decider's coded refusal (FA_TARGET_NAME_REQUIRED)
  name: yup.string().max(200).required(),
  description: words(1000),
  measure: yup
    .string()
    .oneOf([...FA_MEASURE_VALUES])
    .required(),
  // Q-e48f-1 A (2026-10-24): one or more functions; "not sure yet" (`unknown`) only alone — the decider's FA_VALUE_INVALID defence too
  functionHypotheses: yup
    .array()
    .of(
      yup
        .string()
        .oneOf([...FA_FUNCTION_VALUES])
        .required(),
    )
    .min(1)
    .required()
    .test(
      'unknown-alone',
      'functionHypotheses: "unknown" stands alone',
      (v) => !v || !v.includes('unknown') || v.length === 1,
    ),
  baselineWeeks: yup.number().integer().min(0).max(52).nullable(),
});

export const retireTargetBehaviorInputSchema = yup.object({
  subject: userRef.nullable(),
  targetId: idRef.required(),
  reason: words(1000),
});

export const recordBehaviorInstanceInputSchema = yup.object({
  instanceId: uuidV7,
  targetId: idRef.required(),
  happenedAt: yup.date().required(),
  vulnerabilities: yup
    .array()
    .of(
      yup
        .object({
          kind: yup
            .string()
            .oneOf([...FA_VULNERABILITY_KIND_VALUES])
            .required(),
          text: yup.string().max(300).required(),
        })
        .required(),
    )
    .max(20)
    .nullable(),
  promptingEvent: words(1000),
  links: yup
    .array()
    .of(
      yup
        .object({
          kind: yup
            .string()
            .oneOf([...FA_LINK_KIND_VALUES])
            .required(),
          text: yup.string().max(500).required(),
        })
        .required(),
    )
    .max(40)
    .nullable(),
  didBehavior: yup.boolean().required(),
  // a whole number here; whether it is 0–10 is the decider's (FA_URGE_RANGE)
  urge: yup.number().integer().nullable(),
  amount: yup.number().min(0).nullable(),
  consequencesShort: words(1000),
  consequencesLong: words(1000),
  solution: yup
    .object({
      link: words(300),
      instead: words(500),
      tool: yup
        .string()
        .oneOf([...FA_TOOL_VALUES])
        .nullable(),
    })
    .nullable(),
  occurredAt: yup.date().nullable(),
});

export const planContingencyChangeInputSchema = yup.object({
  subject: userRef.nullable(),
  targetId: idRef.required(),
  whatChanges: yup.string().max(1000).required(),
  expectedEffect: words(1000),
  startsOn: localDate.required(),
});

export const markChangeDayInputSchema = yup.object({
  changeId: idRef.required(),
  targetId: idRef.required(),
  day: localDate.required(),
  done: yup
    .string()
    .oneOf([...FA_CHANGE_DONE_VALUES])
    .required(),
  timeZone: yup.string().max(64).nullable(),
});

/** The mutation ARGUMENTS — what the shield's inputRules validate. */
export const setTargetBehaviorArgsSchema = yup.object({
  input: setTargetBehaviorInputSchema.required(),
});
export const retireTargetBehaviorArgsSchema = yup.object({
  input: retireTargetBehaviorInputSchema.required(),
});
export const recordBehaviorInstanceArgsSchema = yup.object({
  input: recordBehaviorInstanceInputSchema.required(),
});
export const planContingencyChangeArgsSchema = yup.object({
  input: planContingencyChangeInputSchema.required(),
});
export const markChangeDayArgsSchema = yup.object({
  input: markChangeDayInputSchema.required(),
});

// Typed loosely on purpose (TS7056 — see diaryCard.ts): the shape is the Yup schemas above.
export const functionalAnalysisValidators: Record<
  | 'setTargetBehaviorInputSchema'
  | 'retireTargetBehaviorInputSchema'
  | 'recordBehaviorInstanceInputSchema'
  | 'planContingencyChangeInputSchema'
  | 'markChangeDayInputSchema'
  | 'setTargetBehaviorArgsSchema'
  | 'retireTargetBehaviorArgsSchema'
  | 'recordBehaviorInstanceArgsSchema'
  | 'planContingencyChangeArgsSchema'
  | 'markChangeDayArgsSchema',
  yup.AnySchema
> = {
  setTargetBehaviorInputSchema,
  retireTargetBehaviorInputSchema,
  recordBehaviorInstanceInputSchema,
  planContingencyChangeInputSchema,
  markChangeDayInputSchema,
  setTargetBehaviorArgsSchema,
  retireTargetBehaviorArgsSchema,
  recordBehaviorInstanceArgsSchema,
  planContingencyChangeArgsSchema,
  markChangeDayArgsSchema,
};
